import { useEffect, useRef, useState, type ReactNode } from "react";

interface RetryImageProps {
  src: string | null | undefined;
  alt: string;
  className?: string;
  /** Rendered once retries are exhausted (or when there's no src). */
  fallback?: ReactNode;
  /** Retry attempts before giving up. */
  maxRetries?: number;
}

/**
 * <img> that retries on load failure. The API host intermittently drops
 * HTTP/2 streams (net::ERR_HTTP2_PROTOCOL_ERROR), especially for larger media;
 * a fresh, cache-busted request usually succeeds — mirroring the retry logic
 * the JSON fetches already use. Falls back gracefully when retries run out.
 */
export function RetryImage({
  src,
  alt,
  className,
  fallback = null,
  maxRetries = 3,
}: RetryImageProps) {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    setAttempt(0);
    setFailed(false);
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, [src]);

  if (!src || failed) {
    return <>{fallback}</>;
  }

  // Cache-bust retries so the browser opens a new stream instead of replaying
  // the failed one.
  const resolvedSrc =
    attempt === 0
      ? src
      : `${src}${src.includes("?") ? "&" : "?"}retry=${attempt}`;

  return (
    <img
      key={resolvedSrc}
      src={resolvedSrc}
      alt={alt}
      className={className}
      onError={() => {
        if (attempt < maxRetries) {
          timerRef.current = window.setTimeout(
            () => setAttempt(a => a + 1),
            400 * (attempt + 1)
          );
        } else {
          setFailed(true);
        }
      }}
    />
  );
}
